import { useState, useRef, useCallback } from 'react';
import { sync } from '../utils/offline/syncEngine';

const PullToRefresh = ({ onRefresh, children }) => {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const containerRef = useRef(null);
  const startY = useRef(0);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const threshold = 80; // Distance needed to trigger refresh

  const handleTouchStart = useCallback((e) => {
    if (!isMobile || refreshing) return;

    // Only start pull if at top of page
    if (window.scrollY === 0) {
      startY.current = e.touches[0].clientY;
      setPulling(true);
    }
  }, [isMobile, refreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!pulling || refreshing) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;

    // Only allow pulling down, not up
    if (diff > 0 && window.scrollY === 0) {
      // Apply resistance to make it feel natural
      const distance = Math.min(diff * 0.5, threshold * 1.5);
      setPullDistance(distance);

      // Prevent default scroll when pulling
      if (distance > 10) {
        e.preventDefault();
      }
    }
  }, [pulling, refreshing, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (!pulling) return;

    if (pullDistance >= threshold && onRefresh) {
      setRefreshing(true);
      setPullDistance(threshold);

      try {
        // A user-initiated refresh is one of the five sync triggers (FR-010).
        // Fire-and-forget: reconciliation must not hold up the gesture, and a
        // failure is reported by the offline banner, not by this component.
        sync().catch(() => {});
        await onRefresh();
      } finally {
        setRefreshing(false);
      }
    }

    setPulling(false);
    setPullDistance(0);
  }, [pulling, pullDistance, threshold, onRefresh]);

  if (!isMobile) {
    return <>{children}</>;
  }

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="min-h-screen"
    >
      {/* Pull indicator */}
      <div
        className="fixed left-0 right-0 flex justify-center items-center z-50 transition-transform duration-200 pointer-events-none"
        style={{
          top: 0,
          height: `${pullDistance}px`,
          opacity: Math.min(pullDistance / threshold, 1),
        }}
      >
        <div className={`flex items-center gap-2 text-gray-600 dark:text-gray-400 ${refreshing ? 'animate-pulse' : ''}`}>
          <svg
            className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`}
            style={{
              transform: refreshing ? 'none' : `rotate(${Math.min((pullDistance / threshold) * 180, 180)}deg)`,
            }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span className="text-sm font-medium">
            {refreshing ? 'Refreshing...' : pullDistance >= threshold ? 'Release to refresh' : 'Pull to refresh'}
          </span>
        </div>
      </div>

      {/* Content with transform when pulling */}
      <div
        style={pullDistance > 0 ? {
          transform: `translateY(${pullDistance}px)`,
          transition: pulling ? 'none' : 'transform 0.2s ease-out',
        } : undefined}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;

